/**
 * Borrador de inspección — persistido en disco.
 *
 * POR QUÉ EXISTE. La pantalla de entrega guardaba las 15 fotos, el combustible,
 * el odómetro, las condiciones, las notas y las DOS firmas en `useState` puro. Si
 * la app se moría a media entrega —y con 15 fotos a resolución de cámara en un
 * teléfono de gama media, Android la mata— se perdía todo y había que empezar de
 * cero, con el anfitrión y el huésped parados junto al carro en la calle. Un
 * gesto de atrás hacía exactamente lo mismo, sin preguntar.
 *
 * El arreglo correcto no es preguntar antes de salir: es que salir no pierda
 * nada. Con el borrador en disco, cerrar la app, irse a WhatsApp a consultar
 * algo, o que el sistema mate el proceso dejan de ser pérdidas.
 *
 * MISMO MECANISMO QUE EL BORRADOR DE RESERVA (`booking.store.ts`): Zustand +
 * `persist` sobre `createPlatformStorage` — MMKV cifrado en nativo, localStorage
 * en web. No se inventa un segundo mecanismo de persistencia para el mismo
 * problema.
 *
 * LO QUE NO RESUELVE SOLO: guardar el `uri` de una foto no sirve si el archivo
 * desaparece. `takePictureAsync` escribe en el directorio de CACHÉ, que el
 * sistema puede vaciar cuando le falta espacio. Por eso el componente de cámara
 * mueve cada captura al directorio de documentos antes de entregarla — ver
 * `InspectionLiveCamera`. Las dos mitades hacen falta: el estado y el archivo.
 */
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { ConditionGrade, InspectionPhoto, InspectionType } from '../types/inspection.types'
import { createPlatformStorage } from '../storage/zustand-storage'
import { isInspectionDraftFresh, pruneExpiredDrafts } from './checkin-draft-prune'

export interface InspectionDraft {
  updatedAt: number
  fuelLevel: number
  /**
   * Si alguien AFIRMÓ el nivel de combustible, o si es el 50% que la pantalla
   * pone por defecto.
   *
   * El combustible era el único dato de la entrega que se registraba sin que
   * nadie lo dijera: el slider arranca en la mitad y, si nadie lo toca, se
   * guarda "50%" como si fuera una lectura. Después la devolución compara
   * contra ese número inventado y de ahí sale un reclamo.
   */
  fuelTouched: boolean
  odometerText: string
  exteriorCondition?: ConditionGrade
  interiorCondition?: ConditionGrade
  notes: string
  photos: InspectionPhoto[]
  guestSignature: string | null
  hostSignature: string | null
}

interface CheckinDraftState {
  /** Un borrador por (renta, tipo): la entrega y la devolución no se pisan. */
  drafts: Record<string, InspectionDraft>
  getDraft: (rentalId: string, type: InspectionType) => InspectionDraft | null
  saveDraft: (rentalId: string, type: InspectionType, patch: Partial<InspectionDraft>) => void
  clearDraft: (rentalId: string, type: InspectionType) => void
  /**
   * Quita del mapa persistido los borradores vencidos. Sin esto el blob crece
   * para siempre y cada cambio lo reescribe entero (ver checkin-draft-prune).
   */
  pruneExpired: () => void
  /**
   * Borra TODOS los borradores. Lo llama `resetClientState` al cerrar sesion.
   *
   * Sin esto, el borrador de la cuenta A sobrevivia al cierre de sesion hacia la
   * cuenta B: quince fotos del carro de otra persona, con su odometro y las dos
   * firmas, dentro de la pantalla de entrega de alguien mas. Lo encontro
   * `scripts/validate-store-reset.mjs`, que existe exactamente para eso.
   */
  reset: () => void
}

export function draftKey(rentalId: string, type: InspectionType): string {
  return `${rentalId}:${type}`
}

const EMPTY: Omit<InspectionDraft, 'updatedAt'> = {
  fuelLevel: 50,
  fuelTouched: false,
  odometerText: '',
  exteriorCondition: undefined,
  interiorCondition: undefined,
  notes: '',
  photos: [],
  guestSignature: null,
  hostSignature: null,
}

export const useCheckinDraftStore = create<CheckinDraftState>()(
  persist(
    (set, get) => ({
      drafts: {},

      getDraft: (rentalId, type) => {
        const draft = get().drafts[draftKey(rentalId, type)]
        return isInspectionDraftFresh(draft, Date.now()) ? draft : null
      },

      saveDraft: (rentalId, type, patch) =>
        set((state) => {
          const key = draftKey(rentalId, type)
          const previo = isInspectionDraftFresh(state.drafts[key], Date.now())
            ? state.drafts[key]
            : undefined
          // Poda en la misma escritura: el blob persistido nunca arrastra
          // inspecciones muertas. El borrador que se acaba de tocar es fresco,
          // así que sobrevive a su propia poda.
          const drafts = pruneExpiredDrafts(state.drafts)
          return {
            drafts: {
              ...drafts,
              [key]: {
                ...EMPTY,
                ...previo,
                ...patch,
                updatedAt: Date.now(),
              },
            },
          }
        }),

      pruneExpired: () =>
        set((state) => {
          const drafts = pruneExpiredDrafts(state.drafts)
          return drafts === state.drafts ? state : { drafts }
        }),

      // Se llama al ENVIAR con éxito, no al salir de la pantalla. Salir no es
      // renunciar: es justo el caso que este store existe para sobrevivir.
      clearDraft: (rentalId, type) =>
        set((state) => {
          const resto = { ...state.drafts }
          delete resto[draftKey(rentalId, type)]
          return { drafts: resto }
        }),

      reset: () => set({ drafts: {} }),
    }),
    {
      name: 'tago.checkin.draft.v1',
      storage: createPlatformStorage<Pick<CheckinDraftState, 'drafts'>>(),
      partialize: (state) => ({ drafts: state.drafts }),
      version: 1,
      // Al abrir, el blob persistido se encoge en la primera lectura en lugar
      // de seguir arrastrando inspecciones de semanas atrás en cada escritura.
      onRehydrateStorage: () => (state) => {
        state?.pruneExpired()
      },
    },
  ),
)
